import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { Platform } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { Catalog, activeCatalogSelector, availableCatalogsSelector } from '@vi-succ/products-domain';
import { ShoppingCartFacade } from '@vi-succ/shoppingcart-domain';
import { Subject, filter, first, map, switchMap, takeUntil, withLatestFrom } from 'rxjs';
import { AuthService } from '../../core/login/services/auth.service';
import { EtCacheService } from '../../services/et-cache.service';
import { EtCatalogDataService } from '../../services/et-catalog-data.service';
import {
  CatalogSwitcherDialogComponent,
  CatalogSwitcherDialogData,
} from '../catalog/catalog-switcher-dialog/catalog-switcher-dialog.component';
import { Language, SalesOrg } from '../catalog/catalog-switcher-dialog/catalog-switcher/catalog-switcher.component';
import { LoginScreenComponent } from './login-screen/login-screen.component';

@Component({
  selector: 'et-setup-login',
  templateUrl: './setup-login.component.html',
  styleUrls: ['./setup-login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LoginScreenComponent, MatButtonModule, MatIconModule, TranslateModule],
})
export class SetupLoginComponent implements OnDestroy {
  private isAuthenticated$ = this.authService.authenticated$;
  private destroy$ = new Subject<void>();
  private isDesktop = this.platform.is('desktop');

  constructor(
    private platform: Platform,
    private store: Store,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<SetupLoginComponent>,
    private authService: AuthService,
    private catalogService: EtCatalogDataService,
    private cartFacade: ShoppingCartFacade,
    private cacheService: EtCacheService,
    private router: Router,
  ) {
    this.isAuthenticated$
      .pipe(
        takeUntil(this.destroy$),
        filter((isAuthenticated) => isAuthenticated),
      )
      .subscribe(() => {
        this.dialogRef.close(true);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onLogin() {
    this.authService.login();
  }

  onRegister() {
    this.authService.register();
  }

  onWithoutLogin() {
    this.store
      .select(availableCatalogsSelector)
      .pipe(
        filter((catalogs) => catalogs.length > 0),
        first(),
        map((catalogs) => {
          // group catalogs by salesOrg
          return catalogs.reduce(
            (acc, catalog) => {
              return {
                ...acc,
                [catalog.salesOrg]: {
                  value: catalog.salesOrg,
                  viewValue: catalog.salesOrg,
                  languages: [
                    ...(acc[catalog.salesOrg]?.languages ?? []),
                    {
                      value: catalog.language,
                      viewValue: catalog.language,
                    },
                  ] as Language[],
                } as SalesOrg,
              };
            },
            {} as { [salesOrg: string]: SalesOrg },
          );
        }),
        withLatestFrom(this.store.select(activeCatalogSelector)),
        switchMap(([salesOrgs, catalog]) => {
          let maxWidth = '100vw';
          let height = '100%';
          const panelClasses = ['menu-dialog-container'];

          if (this.isDesktop) {
            maxWidth = '722px';
            height = 'unset';
            panelClasses.push('desktop-scrollbar');
          }

          return this.dialog
            .open<CatalogSwitcherDialogComponent, CatalogSwitcherDialogData, Catalog | null>(
              CatalogSwitcherDialogComponent,
              {
                data: {
                  salesOrgs,
                  currentCatalog: catalog,
                  showCancel: true,
                  saveButtonLabel: 'Weiter',
                },
                width: '100%',
                maxWidth: maxWidth,
                height: height,
                maxHeight: '100vh',
                panelClass: panelClasses,
              },
            )
            .afterClosed();
        }),
      )
      .subscribe((catalog) => {
        if (catalog) {
          this.catalogService.setActiveCatalog(catalog).then(() => {
            this.cartFacade.resetCart();
          });
          this.cacheService.clearCache();
          this.dialogRef.close(true);
          this.router.navigate(['/']);
        }
      });
  }
}
